import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { IMG, WELCOME_LINES, WELCOME_LINES_EVENING, MARQUEE_ITEMS } from "@/data";
import { Alpana } from "@/components/Logo";
import { speakBlob, stopSpeaking, onAmp } from "@/lib/voice";
import { requestTalk, pollTalk } from "@/lib/talk";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const lineReveal = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { delay: 0.5 + i * 0.3, duration: 0.95, ease: [0.22, 1, 0.36, 1] },
  }),
};

function PathCard({ img, title, sub, latin, onClick, testid, i }) {
  return (
    <motion.button
      data-testid={testid}
      onClick={onClick}
      initial={{ opacity: 0, y: 44 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ delay: i * 0.12, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className="group relative h-[420px] overflow-hidden rounded-2xl border border-gold/20 text-left md:h-[520px]"
    >
      <img
        src={img}
        alt={title}
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.06]"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-dusk via-dusk/20 to-dusk/30 transition-opacity duration-700" />
      <div className="absolute inset-x-0 bottom-0 p-6 sm:p-9">
        <p className="font-latin text-xs italic tracking-[0.25em] text-gold/85">{latin}</p>
        <h3 className="mt-2 font-display text-3xl text-alpana sm:text-4xl">{title}</h3>
        <p className="mt-2 text-sm text-sand/75">{sub}</p>
        <span className="mt-5 inline-flex items-center gap-2 rounded-full border border-alpana/30 bg-dusk/40 px-5 py-2 text-sm text-alpana backdrop-blur-md transition-all duration-500 group-hover:gap-4 group-hover:border-gold group-hover:bg-gold group-hover:text-dusk">
          ভেতরে যান
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M13 5l7 7-7 7" />
          </svg>
        </span>
      </div>
    </motion.button>
  );
}

/* ---------- জীবন্ত আকাশ — তারা আর চাঁদ (HTML canvas) ---------- */
function StarField({ active, full }) {
  const ref = useRef(null);
  const stateRef = useRef({ active, full });
  stateRef.current = { active, full };

  useEffect(() => {
    const cv = ref.current;
    const ctx = cv.getContext("2d");
    let raf, w, h;
    const stars = Array.from({ length: 120 }, () => ({
      x: Math.random(),
      y: Math.random() * 0.6,
      r: 0.3 + Math.random() * 1.2,
      p: Math.random() * Math.PI * 2,
      s: 0.4 + Math.random() * 1.4,
    }));
    const size = () => {
      w = cv.width = cv.offsetWidth;
      h = cv.height = cv.offsetHeight;
    };
    size();
    window.addEventListener("resize", size);
    const draw = (t) => {
      ctx.clearRect(0, 0, w, h);
      const { active: on, full: f } = stateRef.current;
      const I = on ? (f ? 1 : 0.5) : 0;
      if (I > 0) {
        for (const st of stars) {
          const tw = 0.35 + 0.65 * Math.abs(Math.sin(t * 0.001 * st.s + st.p));
          ctx.globalAlpha = tw * I * 0.9;
          ctx.fillStyle = "#F5EAD6";
          ctx.beginPath();
          ctx.arc(st.x * w, st.y * h, st.r, 0, 7);
          ctx.fill();
        }
        // তারার ঝিলিক — চাঁদ নিজের ছবিতেই আছে
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", size);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full"
      style={{
        maskImage: "linear-gradient(to bottom, black 55%, transparent 84%)",
        WebkitMaskImage: "linear-gradient(to bottom, black 55%, transparent 84%)",
      }}
    />
  );
}

/* ---------- জোনাকি — শুধু গেটের প্রাঙ্গণে, মাটির কাছে ---------- */
function Fireflies({ active }) {
  const ref = useRef(null);
  const activeRef = useRef(active);
  activeRef.current = active;

  useEffect(() => {
    const cv = ref.current;
    const ctx = cv.getContext("2d");
    let raf, w, h;
    const flies = Array.from({ length: 18 }, () => ({
      x: Math.random(),
      y: 0.42 + Math.random() * 0.52,
      vx: (Math.random() - 0.5) * 0.0006,
      vy: -(0.0002 + Math.random() * 0.0004),
      p: Math.random() * Math.PI * 2,
      s: 0.8 + Math.random() * 1.6,
      r: 0.8 + Math.random() * 1.3,
    }));
    const size = () => {
      w = cv.width = cv.offsetWidth;
      h = cv.height = cv.offsetHeight;
    };
    size();
    window.addEventListener("resize", size);
    const draw = (t) => {
      ctx.clearRect(0, 0, w, h);
      if (activeRef.current) {
        for (const f of flies) {
          f.x += f.vx;
          f.y += f.vy;
          if (f.y < 0.36) {
            f.y = 1;
            f.x = Math.random();
          }
          if (f.x < -0.02 || f.x > 1.02) f.x = 0.5 + (Math.random() - 0.5);
          const tw = Math.max(0, Math.sin(t * 0.0014 * f.s + f.p));
          if (tw > 0.08) {
            const x = f.x * w, y = f.y * h;
            const g = ctx.createRadialGradient(x, y, 0, x, y, f.r * 5);
            g.addColorStop(0, `rgba(255,185,75,${0.4 * tw})`);
            g.addColorStop(1, "rgba(255,185,75,0)");
            ctx.fillStyle = g;
            ctx.fillRect(x - f.r * 5, y - f.r * 5, f.r * 10, f.r * 10);
            ctx.globalAlpha = tw * 0.9;
            ctx.fillStyle = "#FFD98A";
            ctx.beginPath();
            ctx.arc(x, y, f.r, 0, 7);
            ctx.fill();
            ctx.globalAlpha = 1;
          }
        }
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", size);
    };
  }, []);

  return <canvas ref={ref} aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full" />;
}

/* ---------- প্রদীপ — সুচরিতার হাতে বসানো সন্ধ্যার আনুষ্ঠানিকতা ---------- */
function Pradip({ lit }) {
  return (
    <div
      data-testid="evening-ritual-lamp"
      className="pointer-events-none absolute bottom-0 z-[9]"
      style={{ left: "calc(100% + 12px)" }}
    >
      <motion.div
        initial={false}
        animate={{ opacity: lit ? 1 : 0, scale: lit ? 1 : 0.4 }}
        transition={{ duration: 1.8, ease: "easeOut" }}
        className="relative origin-bottom"
      >
        <div className="absolute -inset-16 animate-glow rounded-full bg-[radial-gradient(circle,rgba(255,170,60,0.5),rgba(255,140,40,0.18)_45%,transparent_70%)] mix-blend-screen" />
        <svg width="34" height="34" viewBox="0 0 40 40" className="relative">
          <defs>
            <radialGradient id="flameG" cx="50%" cy="70%" r="60%">
              <stop offset="0%" stopColor="#FFE9B0" />
              <stop offset="45%" stopColor="#FFB347" />
              <stop offset="100%" stopColor="#C83E2B" />
            </radialGradient>
          </defs>
          {lit && (
            <path d="M20 5 Q26.5 14 20 21 Q13.5 14 20 5Z" fill="url(#flameG)" className="animate-flicker" />
          )}
          <path d="M7 22 Q20 30.5 33 22 L29.5 27.5 Q20 34 10.5 27.5 Z" fill="#6E3423" />
          <ellipse cx="20" cy="22" rx="13.2" ry="3.1" fill="#8B4A32" />
        </svg>
      </motion.div>
    </div>
  );
}

/* সবুজ-পর্দার ভিডিও → canvas-এ সবুজ কেটে স্বচ্ছ — শব্দ ভিডিওর ভেতরেই */
function GreenScreenVideo({ src, muted, videoRef, onError, onEnded, className }) {
  const vidRef = useRef(null);
  const cvRef = useRef(null);

  useEffect(() => {
    const v = vidRef.current;
    const cv = cvRef.current;
    if (!v || !cv) return;
    const ctx = cv.getContext("2d", { alpha: true });
    let raf, stop = false;
    const render = () => {
      if (stop) return;
      if (v.readyState >= 2 && v.videoWidth) {
        if (cv.width !== v.videoWidth || cv.height !== v.videoHeight) {
          cv.width = 480;
          cv.height = 720;
        }
        ctx.drawImage(v, 0, 0, cv.width, cv.height);
        const img = ctx.getImageData(0, 0, cv.width, cv.height);
        const d = img.data;
        for (let i = 0; i < d.length; i += 4) {
          const r = d[i], g = d[i + 1], b = d[i + 2];
          const greenness = g - Math.max(r, b);
          if (greenness > 60) d[i + 3] = 0;
          else if (greenness > 25) d[i + 3] = Math.max(0, 255 - (greenness - 25) * 6);
          if (g > r && g > b) {
            const avg = (r + b) >> 1;
            if (g > avg) d[i + 1] = avg + ((g - avg) * 0.4) | 0;
          }
        }
        ctx.putImageData(img, 0, 0);
      }
      raf = requestAnimationFrame(render);
    };
    raf = requestAnimationFrame(render);
    return () => {
      stop = true;
      cancelAnimationFrame(raf);
    };
  }, [src]);

  return (
    <>
      <video
        ref={(el) => {
          vidRef.current = el;
          if (videoRef) videoRef.current = el;
          // React-এর muted-কুইর্ক — অটোপ্লে নিশ্চিত করতে সরাসরি প্রপার্টি
          if (el) el.muted = muted;
        }}
        src={src}
        autoPlay
        muted={muted}
        playsInline
        onEnded={onEnded}
        onError={onError}
        className="pointer-events-none absolute left-0 top-0 h-1 w-1 opacity-0"
      />
      <canvas ref={cvRef} width="480" height="720" className={className} data-testid="sucharita-figure-video" />
    </>
  );
}

// Sucharita walks in through the big gate — পায়ে হেঁটে, কাছে এসে দাঁড়ায়
function SucharitaFigure({ videoUrl, muted, showSound, onSound, videoRef, evening, lampLit, onVideoError, onVideoEnded, walking }) {
  const innerRef = useRef(null);
  useEffect(
    () =>
      onAmp((a) => {
        if (innerRef.current && !videoUrl)
          innerRef.current.style.transform = `scale(${1 + a * 0.025})`;
      }),
    [videoUrl],
  );
  return (
    <div
      className="absolute z-[8] left-1/2 -translate-x-1/2 top-[calc(80svh-345px)] sm:top-[calc(80svh-375px)] lg:top-auto lg:bottom-[6%] sm:left-[57%]"
      data-testid="sucharita-figure"
    >
      {evening && (
        <div className="pointer-events-none absolute -inset-12 rounded-full bg-[radial-gradient(circle,rgba(255,170,60,0.15),transparent_62%)] mix-blend-screen" />
      )}
      <motion.div
        initial={{ scale: 0.46, y: "-8%", opacity: 0 }}
        animate={{ scale: 1, y: "0%", opacity: 1 }}
        transition={{ delay: 0.7, duration: 3.6, ease: [0.45, 0.05, 0.35, 1] }}
        className="origin-bottom"
      >
      <div className={`relative aspect-[2/3] w-[220px] sm:w-[240px] lg:w-[280px] ${walking ? "walk-bob" : ""}`}>
        <div className="absolute inset-x-12 bottom-1 h-4 rounded-[100%] bg-black/45 blur-md" />
        <div ref={innerRef} className="relative h-full w-full will-change-transform">
          {videoUrl ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.2 }}
              className="absolute inset-0"
            >
              <GreenScreenVideo
                src={videoUrl}
                muted={muted}
                videoRef={videoRef}
                onError={onVideoError}
                onEnded={onVideoEnded}
                className="figure-feather h-full w-full"
              />
            </motion.div>
          ) : (
            <img
              src="/sucharita-figure.png"
              alt="সুচরিতা"
              data-testid="sucharita-figure-img"
              className="figure-feather absolute inset-0 h-full w-full object-contain object-bottom"
            />
          )}
        </div>
        {showSound && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            data-testid="sucharita-sound"
            onClick={onSound}
            className="absolute -bottom-2 left-1/2 flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-full border border-gold/40 bg-dusk/80 px-3 py-1 text-[10px] text-sand backdrop-blur-md transition-colors hover:text-gold"
          >
            <span className="h-1.5 w-1.5 animate-flicker rounded-full bg-vermilion" />
            গলা চালু করুন
          </motion.button>
        )}
      </div>
      </motion.div>
      {evening && <Pradip lit={lampLit} />}
    </div>
  );
}

export default function Gate({ navTo, phase = "day", site }) {
  const S = site?.settings || {};
  // প্রবেশের মুহূর্তের আবহই থাকবে — পরে টগল করলেও স্বাগতম বদলায় না
  const [eve] = useState(() => phase !== "day");
  const LINES = eve
    ? S.welcome_evening?.length
      ? S.welcome_evening
      : WELCOME_LINES_EVENING
    : S.welcome_day?.length
      ? S.welcome_day
      : WELCOME_LINES;

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  // মাউসের সঙ্গে হালকা দৃষ্টি-গভীরতা
  const mx = useMotionValue(0);
  const smx = useSpring(mx, { stiffness: 42, damping: 15 });
  const pxStars = useTransform(smx, [-1, 1], [12, -12]);
  const pxFlies = useTransform(smx, [-1, 1], [20, -20]);
  const pxMist = useTransform(smx, [-1, 1], [-16, 16]);
  const pxReeds = useTransform(smx, [-1, 1], [6, -6]);
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width) * 2 - 1);
  };

  const figRef = useRef(null);
  const [figVideo, setFigVideo] = useState(null);
  const [figMuted, setFigMuted] = useState(true);
  const [silent, setSilent] = useState(true);
  const [lampLit, setLampLit] = useState(false);
  const [walking, setWalking] = useState(true);
  const voiceStarted = useRef(false);
  const prewarmed = useRef(false);
  const [siteReady, setSiteReady] = useState(false);

  // অ্যাডমিন-সাধা লেখা এলে সেটাই স্বাগতম হবে (না এলে ১.৫ সেকেন্ডে ডিফল্ট)
  useEffect(() => {
    if (site) setSiteReady(true);
  }, [site]);
  useEffect(() => {
    const t = setTimeout(() => setSiteReady(true), 1500);
    return () => clearTimeout(t);
  }, []);

  // হাঁটা শেষে দাঁড়ানো
  useEffect(() => {
    const t = setTimeout(() => setWalking(false), 4400);
    return () => clearTimeout(t);
  }, []);

  // সন্ধ্যার ঘোষণা — সুচরিতা এসে পথের প্রদীপ জ্বালায়, গ্রাম আলোয় ভরে ওঠে
  useEffect(() => {
    if (!eve) return;
    const t = setTimeout(() => setLampLit(true), 4300);
    return () => clearTimeout(t);
  }, [eve]);

  // pre-warm her welcome lip-sync video the instant the page loads
  useEffect(() => {
    if (!siteReady || prewarmed.current) return; // StrictMode-এর দ্বৈত-চালানিতে গলা দ্বিগুণ হয় না
    prewarmed.current = true;
    const text = LINES.join(" ");
    requestTalk(text, "figure").then(async (talk) => {
      if (talk?.video_url) {
        // হাঁটা শেষ হলে ঠোঁটের-ভিডিও — শব্দ ভিডিওর ভেতরেই, ঠোঁট-গলা কখনো আলাদা হয় না
        setTimeout(() => setFigVideo(talk.video_url), 4500);
        return;
      }
      if (talk?.talk_id) {
        // ভিডিও প্রস্তুত হলে গলা বন্ধ করে ঠোঁটের-ভিডিওতে যাওয়া
        pollTalk(talk.talk_id).then((url) => {
          if (url) {
            stopSpeaking();
            setFigVideo(url);
          }
        });
        // ভিডিও তৈরি হওয়া পর্যন্ত তিনি নিজের গলায়ই স্বাগতম জানান
        try {
          if (voiceStarted.current) return;
          voiceStarted.current = true;
          const res = await fetch(`${API}/host/voice`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ text }),
          });
          if (res.ok) {
            await speakBlob(await res.blob());
            setSilent(false);
          }
        } catch {
          setSilent(true);
        }
        return;
      }
      // lip-sync studio unavailable → she still speaks in her own voice
      try {
        const res = await fetch(`${API}/host/voice`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text }),
        });
        if (res.ok) {
          await speakBlob(await res.blob());
          setSilent(false);
        }
      } catch {
        setSilent(true);
      }
    });
  }, [siteReady]);

  const unmute = async () => {
    const v = figRef.current;
    if (!v) return;
    v.muted = false;
    try {
      await v.play();
      setFigMuted(false);
      setSilent(false);
    } catch {
      v.muted = true;
      setFigMuted(true);
    }
  };

  const handleSound = async () => {
    voiceStarted.current = true;
    if (figVideo) {
      stopSpeaking();
      return unmute();
    }
    try {
      const res = await fetch(`${API}/host/voice`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: LINES.join(" ") }),
      });
      if (res.ok) {
        await speakBlob(await res.blob());
        setSilent(false);
      }
    } catch {
      setSilent(true);
    }
  };

  // any first tap lets her speak out loud
  useEffect(() => {
    const kick = () => handleSound();
    window.addEventListener("pointerdown", kick, { once: true });
    return () => window.removeEventListener("pointerdown", kick);
  }, [figVideo]);

  return (
    <div>
      {/* ---------- MAIN GATE — লেয়ার-করা জীবন্ত দৃশ্য ---------- */}
      <section
        ref={heroRef}
        onPointerMove={onMove}
        className="relative flex min-h-screen items-center overflow-hidden"
        data-testid="gate-hero"
      >
        {/* স্তর ১ — ফটোরিয়ালিস্টিক গেট: দিন / গোধূলি / রাত */}
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
          <img
            src={S.gate_day || IMG.gate}
            alt=""
            data-testid="gate-hero-image"
            className={`gate-phase-img absolute inset-0 h-full w-full object-cover ${phase === "day" ? "opacity-100" : "opacity-0"}`}
          />
          <img
            src={S.gate_dusk || IMG.gate_dusk}
            alt=""
            data-testid="gate-dusk-image"
            className={`gate-phase-img absolute inset-0 h-full w-full object-cover ${phase === "dusk" ? "opacity-100" : "opacity-0"}`}
          />
          <img
            src={S.gate_night || IMG.gate_night}
            alt=""
            data-testid="gate-night-image"
            className={`gate-phase-img absolute inset-0 h-full w-full object-cover ${phase === "night" ? "opacity-100" : "opacity-0"}`}
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-dusk/95 via-dusk/45 to-dusk/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-dusk via-transparent to-dusk/60" />

        {/* স্তর ২ — তারাভরা আকাশ ও চাঁদ */}
        <motion.div style={{ x: pxStars }} className="absolute inset-0 z-[3]">
          <StarField active={phase !== "day"} full={phase === "night"} />
        </motion.div>

        {/* স্তর ২ — জোনাকি (শুধু প্রাঙ্গণে, সন্ধ্যা-রাতে) */}
        <motion.div style={{ x: pxFlies }} className="absolute inset-0 z-[4]">
          <Fireflies active={phase !== "day"} />
        </motion.div>

        {/* স্তর ৩ — ভাসমান কুয়াশা */}
        <motion.div
          style={{ x: pxMist }}
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-[5] overflow-hidden"
        >
          {phase !== "day" && (
            <>
              <div className="mist-blob animate-mist" style={{ bottom: "11%", left: "-10%", width: "60%", height: "22%" }} />
              <div className="mist-blob animate-mist" style={{ bottom: "5%", right: "-12%", width: "55%", height: "20%", animationDuration: "46s", animationDelay: "-14s" }} />
              <div className="mist-blob animate-mist" style={{ bottom: "16%", left: "28%", width: "42%", height: "16%", animationDuration: "54s", animationDelay: "-26s" }} />
            </>
          )}
        </motion.div>

        {/* স্তর ৫ — প্রদীপের আলোর পুল (সন্ধ্যায়) */}
        <motion.div
          style={{ x: pxFlies }}
          aria-hidden="true"
          animate={{ opacity: phase !== "day" ? 1 : 0 }}
          transition={{ duration: 1.6, delay: phase !== "day" ? 1.2 : 0 }}
          className="pointer-events-none absolute inset-0 z-[5]"
        >
          {[
            { left: "16%", bottom: "9%", width: 340, height: 190 },
            { left: "45%", bottom: "3%", width: 430, height: 210 },
            { left: "72%", bottom: "11%", width: 320, height: 180 },
          ].map((p, i) => (
            <div key={i} className="lamp-pool" style={p} />
          ))}
        </motion.div>
        <div className="night-glow" />

        {/* স্তর ৬ — সামনের ঘাস-নরেনের সিলুয়েট */}
        <motion.div
          style={{ x: pxReeds }}
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 z-[6]"
        >
          <svg viewBox="0 0 1440 110" preserveAspectRatio="none" className="h-20 w-full text-[#100A07]/95 sm:h-24" fill="currentColor">
            <path d="M0 110 L0 88 Q70 72 140 84 T280 80 T420 92 T560 76 T700 88 T840 78 T980 90 T1120 74 T1260 88 T1440 80 L1440 110 Z" />
            <path d="M60 110 Q64 84 58 66 M72 110 Q78 88 74 70 M300 110 Q306 82 300 64 M312 110 Q316 86 322 68 M660 110 Q654 84 660 66 M672 110 Q678 88 684 72 M1020 110 Q1026 84 1020 68 M1032 110 Q1038 86 1044 70 M1300 110 Q1306 82 1300 66 M1312 110 Q1318 88 1324 72" stroke="currentColor" strokeWidth="2.4" fill="none" strokeLinecap="round" />
          </svg>
        </motion.div>

        {/* Sucharita stands on the front path of the gate */}
        <SucharitaFigure
          videoUrl={figVideo}
          muted={figMuted}
          showSound={silent}
          onSound={handleSound}
          videoRef={figRef}
          evening={eve}
          lampLit={lampLit}
          onVideoError={() => setFigVideo(null)}
          onVideoEnded={() => setFigVideo(null)}
          walking={walking}
        />

        <motion.div
          style={{ opacity: fade }}
          className="relative z-10 mx-auto grid w-full max-w-[1400px] grid-cols-1 items-center gap-10 px-6 pb-24 pt-28 sm:px-10 lg:grid-cols-12 lg:gap-6"
        >
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25, duration: 0.7 }}
              className="flex items-center gap-3 text-xs tracking-[0.3em] text-gold/85"
            >
              <span className="h-px w-10 bg-gold/60" />
              {S.tagline || "বাংলার ডিজিটাল সাংস্কৃতিক গ্রাম"}
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 26 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="mt-5 font-display text-6xl leading-[1.05] text-alpana sm:text-7xl lg:text-8xl"
            >
              {S.brand_title || "রং ও মাটি"}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.8 }}
              className="mt-3 font-latin text-sm italic tracking-[0.28em] text-sand/60"
            >
              {eve ? (S.latin_evening || "GOOD EVENING — THE VILLAGE LAMPS ARE LIT") : (S.latin_tagline || "A DIGITAL CULTURAL VILLAGE")}
            </motion.p>

            {/* মোবাইলে সুচরিতার মুখ কার্ডের পেছনে না যায় */}
            <div className="h-[38vh] lg:h-0" />

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 3.1, duration: 0.8 }}
              className="relative mt-9 max-w-lg rounded-2xl border border-gold/25 bg-plaster/80 p-5 shadow-2xl backdrop-blur-xl"
              data-testid="hero-welcome-card"
            >
              <div className="flex items-start gap-4">
                <img
                  src={IMG.host}
                  alt="সুচরিতা"
                  className="h-11 w-11 shrink-0 rounded-full object-cover object-top ring-2 ring-gold/50"
                />
                <div className="min-w-0">
                  <p className="text-xs tracking-wide text-gold/80">
                    সুচরিতা <span className="text-sand/40">{eve ? "• সন্ধ্যার প্রদীপ-স্বাগতম" : "• আপনার সঙ্গী"}</span>
                  </p>
                  <div className="mt-1.5">
                    {LINES.map((line, i) => (
                      <span className="reveal-mask" key={i}>
                        <motion.span
                          custom={i}
                          variants={lineReveal}
                          initial="hidden"
                          animate="show"
                          className="block font-display text-lg leading-relaxed text-sand/95"
                        >
                          {line}
                        </motion.span>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 4.3, duration: 0.6 }}
                className="mt-4 flex flex-wrap gap-2.5"
              >
                <button
                  data-testid="gate-hero-chip-haat"
                  onClick={() => navTo("hat", "রঙের হাট")}
                  className="rounded-full border border-gold/40 bg-gold/10 px-4 py-2 text-sm text-gold transition-all duration-300 hover:bg-gold hover:text-dusk"
                >
                  রঙের হাটে যাই
                </button>
                <button
                  data-testid="gate-hero-chip-moncho"
                  onClick={() => navTo("moncho", "রং ও মঞ্চ")}
                  className="rounded-full border border-gold/40 bg-gold/10 px-4 py-2 text-sm text-gold transition-all duration-300 hover:bg-gold hover:text-dusk"
                >
                  রং ও মঞ্চে যাই
                </button>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>

        <motion.button
          data-testid="scroll-cue"
          onClick={() => document.getElementById("paths")?.scrollIntoView({ behavior: "smooth" })}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.1, duration: 0.8 }}
          className="absolute bottom-6 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-2 text-sand/70 transition-colors hover:text-gold"
        >
          <span className="text-[11px] tracking-[0.25em]">ভেতরে যান</span>
          <motion.span animate={{ y: [0, 7, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 4v16M5 13l7 7 7-7" />
            </svg>
          </motion.span>
        </motion.button>
      </section>

      {/* ---------- EDITORIAL MARQUEE ---------- */}
      <div className="relative overflow-hidden border-y border-gold/15 bg-plaster/70 py-4">
        <div className="animate-marquee flex w-max">
          {[0, 1].map((n) => (
            <div key={n} aria-hidden={n === 1} className="flex shrink-0 items-center">
              {(S.marquee?.length ? S.marquee : MARQUEE_ITEMS).map((item) => (
                <span key={item + n} className="flex items-center">
                  <span className="whitespace-nowrap px-7 font-display text-xl text-sand/65">{item}</span>
                  <span className="h-1.5 w-1.5 rounded-full bg-gold/70" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ---------- TWO PATHS ---------- */}
      <section id="paths" className="mx-auto max-w-[1400px] px-6 py-20 sm:px-10 sm:py-28">
        <div className="mb-12 flex flex-col items-center text-center">
          <Alpana className="w-32" />
          <h2 className="mt-6 font-display text-3xl text-sand sm:text-5xl">গ্রামের ভেতরে দুটি পথ</h2>
          <p className="mt-3 font-latin text-sm italic tracking-wide text-gold/70">
            Two paths lead into the village — choose yours
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <PathCard
            testid="entrance-card-haat"
            img={IMG.lane}
            title="রঙের হাট"
            sub="হস্তশিল্প • পোশাক • গৃহসজ্জা • উপহার"
            latin="The Craft Market"
            onClick={() => navTo("hat", "রঙের হাট")}
            i={0}
          />
          <PathCard
            testid="entrance-card-moncho"
            img={IMG.moncho}
            title="রং ও মঞ্চ"
            sub="বাউল • লোকসঙ্গীত • নৃত্য • পরিবেশনা"
            latin="The Folk Stage"
            onClick={() => navTo("moncho", "রং ও মঞ্চ")}
            i={1}
          />
        </div>
      </section>
    </div>
  );
}
